import React, { useCallback, useId, useMemo, useRef } from 'react';
import { Modal } from '@/components/Modals/modal';
import { US_STATES } from '@/constants';
import { Address, ModalDataTypes, Recipient } from '@/context/types';
import { useAppState } from '@/context/appState';
import { FloatingInput } from '@/components/Input/Floating/FloatingInput';
import { FloatingSelect } from '@/components/Input/Floating/FloatingSelect';
import { useLocalityLock } from '@/hooks/useConfirmAddress';
import { ModalBody, ModalImpl } from '../ModalFactory';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatSecondaryAddress } from '@/helpers/misc';
import useUser from '@/hooks/useUser';

type EDModalData = ModalDataTypes['AddressConfirm'];

export const EditAddressModal: ModalImpl = (props) => {
  return (
    <Modal
      variant="form"
      ariaLabel="Edit Address"
      title="Edit Address"
      className=""
      {...props}
    >
      {EditAddress}
    </Modal>
  );
};

const EditAddress: ModalBody<EDModalData> = ({ withModalClose, modalData }) => {
  const {
    fullName = '',
    address: {
      street = '',
      secondary_address = '',
      postcode = '',
      place = '',
      region = '',
      region_code = '',
      country = 'United States',
      country_code = 'US',
      value: mapbox_id = '',
      label: full_address = '',
    } = {},
    instructions = '',
    onAddressConfirmed,
    hideSave = false,
  } = modalData ?? {};
  const { user } = useUser();

  const { LocalitySelectLock } = useLocalityLock();
  const formRef = useRef<HTMLFormElement>(null);

  // Replaces isDefaultAddress
  const isDefault = useMemo(() => {
    const defaultAddr = user?.defaultAddress;
    if (!defaultAddr) return false;

    // 1. Primary Check: If MapBox IDs exist and match, it's the exact same address
    if (mapbox_id && defaultAddr.value === mapbox_id) {
      return true;
    }

    // 2. Fallback Check: Compare the exact string fields just in case 'value' is missing
    return (
      (defaultAddr.street || '') === (street || '') &&
      (defaultAddr.postcode || '') === (postcode || '') &&
      (defaultAddr.place || '') === (place || '') &&
      (defaultAddr.region || '') === (region || '') &&
      (defaultAddr.secondary_address || '') === (secondary_address || '')
    );
  }, [
    user?.defaultAddress,
    mapbox_id,
    street,
    postcode,
    place,
    region,
    secondary_address,
  ]);

  const handleSubmit = withModalClose(async (e: React.FormEvent) => {
    // 1. Prevent the page from refreshing
    e.preventDefault();

    // 2. Extract data using the form element (event.target)
    const formData = new FormData(e.target as HTMLFormElement);

    // 3. Convert to a plain JavaScript object
    const details: any = Object.fromEntries(formData);

    // Format Address Input
    const secondaryAddress = formatSecondaryAddress({
      secondary_address: details.secondary_address,
      aptSuite: details.aptSuite,
    });
    const formattedAddr = {
      street: details.street,
      secondary_address: secondaryAddress,
      postcode: details.postcode,
      place: details.place,
      region: details.region,
      region_code: details.region_code,
      country: 'United States' as 'United States',
      country_code: 'US' as 'US',
      label: details.label,
      value: details.value,
    };

    // Confirmed Callback
    await onAddressConfirmed!({
      ...formattedAddr,
      defaultDeliveryCheckbox: details.defaultDeliveryCheckbox,
      recipient: details.fullName,
    } as Address & {
      recipient: string;
      defaultDeliveryCheckbox: boolean;
      instructions: string;
    });
  });

  // Tracking Changed Default
  const hasAutoUnchecked = useRef(false);

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      onChange={(e) => {
        if (!user) return;
        // 1. If the user clicks the checkbox itself, they've taken manual control.
        // We flag it as "handled" so we don't override their explicit choice later.
        if ((e.target as HTMLInputElement).name === 'defaultDeliveryCheckbox') {
          hasAutoUnchecked.current = true;
          return;
        }

        // 2. On the VERY FIRST text input change, uncheck the box.
        if (!hasAutoUnchecked.current) {
          const checkbox = e.currentTarget.elements.namedItem(
            'defaultDeliveryCheckbox',
          ) as HTMLInputElement;

          if (checkbox && checkbox.checked) {
            checkbox.checked = false;
          }

          // Lock the flag so this never runs again for the lifespan of the modal
          hasAutoUnchecked.current = true;
        }
      }}
      className="space-y-[30px] w-full"
    >
      <FloatingInput
        id="fullName"
        name="recipient"
        label="Recipient Name"
        required
        defaultValue={fullName}
        autoFocus
      />

      <FloatingInput
        id="street"
        name="street"
        label="Street Address"
        required
        defaultValue={street}
      />

      <FloatingInput
        id="secondary_address"
        name="secondary_address"
        label="2nd Street Address (Optional)"
        defaultValue={secondary_address}
      />

      <div className="flex flex-col sm:flex-row justify-between gap-x-2.5 space-y-[30px] sm:space-y-0">
        <div className="sm:w-1/2">
          <FloatingInput
            id="aptSuite"
            name="aptSuite"
            label="Apt/Suite (optional)"
            defaultValue={''}
          />
        </div>
        <div className="sm:w-1/2">
          <FloatingInput
            id="postcode"
            name="postcode"
            label="Zip Code"
            required
            defaultValue={postcode}
          />
        </div>
      </div>

      <FloatingInput
        id="place"
        name="place"
        label="City"
        required
        defaultValue={place}
      />

      {/* State Dropdown Trigger mapped to Mapbox 'region' */}
      <div className="relative flex items-center w-full">
        <FloatingSelect
          label="State"
          name="region_select"
          hiddenNames={{ label: 'region', value: 'region_code' }}
          options={US_STATES}
          defaultValue={{ label: region, value: region_code }}
          {...LocalitySelectLock}
        />
      </div>

      {/* Delivery Instructions */}
      <div className="relative flex items-center w-full">
        <textarea
          name="instructions"
          placeholder="Delivery Instructions (optional)"
          defaultValue={instructions}
          className="w-full p-[15px] border border-grey-20 rounded-[10px] h-20 bg-transparent z-[1] focus:ring-2 focus:ring-black outline-none transition-shadow resize-none"
        />
      </div>

      {/* Default Delivery Checkbox */}
      {!hideSave && user && (
        <div className="flex items-center">
          <input
            type="checkbox"
            id="defaultDeliveryCheckbox"
            name="defaultDeliveryCheckbox"
            defaultChecked={isDefault}
            className="mr-2 size-4 cursor-pointer accent-black rounded-sm border-grey-20"
          />
          <label
            htmlFor="defaultDeliveryCheckbox"
            className="text-grey-60 cursor-pointer select-none"
          >
            <p className="font-normal text-[14px] leading-[18px] 2xl:text-[16px] 2xl:leading-[20px] m-0">
              Make this my default delivery address
            </p>
          </label>
        </div>
      )}
      {/* Submit Button */}
      <div className="w-full flex flex-col sm:flex-row px-auto gap-2 sm:gap-10 items-center pb-5 sm:pb-0">
        <button
          type="submit"
          className="flex-nowrap inline-flex items-center rounded-full py-3 max-w-[400px] min-h-[40px] h-[40px] sm:min-h-[50px] sm:h-[50px] bg-black text-white hover:bg-black/80 transition-colors pl-[30px] pr-[30px] justify-center w-full sm:max-w-[363px] mx-auto"
        >
          <span className="font-bold text-[16px] leading-[20px] 2xl:text-[18px] 2xl:leading-[22px] text-nowrap w-full">
            Save Address
          </span>
        </button>
      </div>
    </form>
  );
};
